import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// The SUBPATH import is load-bearing: the package index re-exports the zod
// schemas, and zod has no business in the web bundle (FR-1.2's budget).
import { MUTUAL_KNOCK_NOTICE } from "@nob/schemas/notices";
import {
  fetchKnock,
  fetchKnockMessages,
  fetchReceipt,
  fetchStanding,
  sendKnockMessage,
  uploadAttachment,
  type KnockGate,
  type Receipt,
  type Standing,
} from "../../lib/api";
import { qk, planQuery } from "../../lib/queries";
import { useRoomAdded } from "../../lib/room";
import { streamTurn } from "../../lib/sse";
import { track } from "../../lib/telemetry";
import { attachmentFailureLine, localRefusal, ONE_AT_A_TIME } from "./attachment";
import { PAYMENT_SUSPENDED_COPY } from "./copy";
import { ChatShell, DoorHeader, SystemRow, transcriptLines, type ThreadLine } from "./chat";
import { VOICE_UNHEARD_COPY } from "./voice";
import { QuietButton } from "../ui/button";
import { LIVE_THREAD_REFRESH_MS } from "../shell/use-active-knock";
import { BACK_TO_DOOR_COPY, KnockThread } from "./thread";

/** A message to the person behind the door that did not go — the room's fault. */
export const PERSON_SEND_FAILED = "That did not go. Say it again.";
/**
 * The other refusal, and it is not the room's fault: the server says
 * `KNOCK_NOT_FOUND` for a write-back on a door that is not open, so a screen
 * that got its composer from a stale gate is told what is actually true rather
 * than being invited to retype into a closed door. The next poll puts the
 * doorman's composer back under it.
 */
export const PERSON_SEND_CLOSED = "The door isn't open yet.";
import type { WorkItemState } from "./work-indicator";

/** What the thread says while it is going to fetch a knock it was handed. */
export const BOOTING_COPY = "Nob is finding your knock.";

/** The stumble line: said in Nob's voice, and the typed message comes back with it. */
export const RETRY_COPY = "The Nob lost its train of thought. Say that again.";

type Props = {
  knockId: string;
  handle: string;
  /**
   * The doorman's opening line, when the knock has just been started and the
   * transcript is therefore already known. `null` on re-entry, which is the
   * signal to go and read the conversation back from the server.
   */
  opening: string | null;
  /**
   * THE AI DISCLOSURE, above the first line of the thread (EU AI Act Art. 50;
   * ruling R-0914-1 (11)). Server-authored and rendered verbatim. Known already
   * on a knock that has just opened — it rides on the start response — and read
   * back with the transcript on re-entry; empty until one of the two arrives,
   * which draws nothing rather than a sentence this side made up.
   */
  disclosure?: string;
  turnsUsed: number | null;
  maxTurns: number | null;
  /**
   * The conversation that got the sender here — the intake, as it was said
   * (M8). The grilling continues the same thread rather than opening a new one,
   * so the lines above the doorman's first question are simply handed over.
   * Empty on re-entry, where the transcript is read back from the server.
   */
  prelude?: ThreadLine[];
  /**
   * What the sender wrote at this door BEFORE they had an account, handed back
   * as the composer's opening contents (`pre-auth.tsx`). Empty for every
   * ordinary knock, and never sent by itself — it is a draft, and the send is
   * still a person pressing send.
   *
   * THE DISTINCTION FROM `firstTurn` IS TIME (owner item 7, 2026-08-27). These
   * words were typed at a poster, then survived a trip to an identity provider
   * and back — minutes, a consent screen and a different origin ago. Nobody
   * pressed send on them at this door; they were merely rescued, and putting
   * them back in the box is the honest thing to do with a rescued draft.
   */
  prefill?: string;
  /**
   * What the sender typed and PRESSED SEND ON one moment ago, in the console's
   * compose thread — delivered as their first turn, not handed back as a draft
   * (owner item 7).
   *
   * The old shape prefilled here too, and it read as a bug because it was one:
   * the send that opened the hearing did not send anything, and the words came
   * back needing a second press nobody had asked for. Sending them is not this
   * product speaking for anybody — they are the exact words whose Send was just
   * pressed, and the doorman's opening question is a question about the door
   * they were written for.
   */
  firstTurn?: string;
  /** The door's name, so the header does not change at the handover. */
  doorName?: string | null;
  /** The door owner's picture, for the face beside their bubbles (wave 90). */
  doorAvatar?: string | null;
  onSessionExpired: () => void;
  /** The knock is not there any more: back to the door, not to a dead thread. */
  onGone: () => void;
  onLeave: () => void;
  /**
   * Open the plan view at its options, for the out-of-room row's one control
   * ("Get more usage"). A PROP rather than a `useShellUi()` read, because this
   * screen is otherwise free of the shell and a hidden context dependency is a
   * screen that cannot be rendered alone. Absent means the row offers nothing
   * to press.
   */
  onOpenPlans?: (() => void) | undefined;
  /** Open the plan room at the trial's consent, for the out-of-room row (ruling R-1006-2). */
  onStartTrial?: (() => void) | undefined;
  /** The shell owns the header and the height — see `ChatShell`'s `fill`. */
  embedded?: boolean;
  /** Anything the surface wants above the first line of the transcript. */
  above?: ReactNode;
};

/**
 * What a refusal says, in Nob's words (§5.3 codes). `null` means the code is
 * not the thread's to answer — the caller handles it some other way.
 */
export function turnFailureLine(code: string): string | null {
  switch (code) {
    /**
     * MONEY IS ANSWERED BY THE OUT-OF-ROOM ROW, NEVER BY A STUMBLE (`copy.ts`,
     * ruling R-0826-8). A turn after the read pays for itself as it lands
     * (§4.4), so this arrives mid-conversation — and the default arm below used
     * to catch it and say "The Nob lost its train of thought. Say that again.",
     * which invites somebody with no room left to retype the same words for
     * ever. The row's one control is the road, and the turn goes back up by
     * itself once there is room again.
     */
    case "PLAN_EXHAUSTED":
    case "ALLOWANCE_PAUSED":
      return null;
    /** The other money refusal: nothing can be bought past it, so it is a line. */
    case "PAYMENT_RISK_SUSPENDED":
      return PAYMENT_SUSPENDED_COPY;
    case "VALIDATION_ERROR":
      return "That is longer than Nob will read. Two thousand characters.";
    case "INPUT_REFUSED":
      return "That is not going to work here. Say what you actually want.";
    case "KNOCK_ACTIVE":
      return "Nob is still answering the last one.";
    // The doorman is not listening any more: the door opened (the write-back
    // lane is the road), the sender was blocked, or the guard stopped it (§3,
    // §8). Only the server knows which, so the screen asks rather than saying.
    case "KNOCK_CLOSED":
      return null;
    case "RATE_LIMITED":
      return "That’s enough knocking for today.";
    case "UNAUTHENTICATED":
    case "KNOCK_NOT_FOUND":
      return null;
    // The room's fault, not the sender's. "Say that again" would invite
    // retyping the same message into the same dead model forever — which is
    // exactly how this line was found.
    case "PROVIDER_UNAVAILABLE":
      return "The doorman cannot reach its model right now. Not you — try again in a moment.";
    default:
      return RETRY_COPY;
  }
}

/**
 * Screen 4 — the grilling (M9, PRD §5.3, FR-3.3). The turn is streamed, so
 * this component owns the only three facts the thread below it cannot derive:
 * what has been said, what is being said right now, and whether the sender may
 * still speak.
 *
 * Nothing here is authoritative. The transcript is read back from the server on
 * re-entry, the status comes from the stream's own `done`, and a message that
 * fails to land is handed back to the field it was typed in rather than being
 * quietly lost.
 */
export function KnockStartedScreen({
  knockId,
  handle,
  opening,
  disclosure = "",
  turnsUsed,
  maxTurns,
  prelude,
  prefill = "",
  firstTurn = "",
  doorName = null,
  doorAvatar = null,
  onSessionExpired,
  onGone,
  onLeave,
  onOpenPlans,
  onStartTrial,
  embedded = false,
  above = null,
}: Props) {
  const queryClient = useQueryClient();
  const [lines, setLines] = useState<ThreadLine[]>(() => [
    ...(prelude ?? []),
    ...(opening ? [{ role: "doorman" as const, content: opening }] : []),
  ]);
  const [used, setUsed] = useState(turnsUsed ?? 0);
  const [of, setOf] = useState(maxTurns ?? 0);
  /** The turn the server is reporting mid-stream, while it is reporting one. */
  const [inFlight, setInFlight] = useState<number | null>(null);
  /**
   * The three facts the lifecycle turns on (`docs/conversation-model.md` §6.1),
   * and not one of them is a status.
   *
   * `gate` is the recipient's decision and moves only when they act; `assessed`
   * is the doorman having published a read; `guardClosed` is the guard having
   * stopped it listening. The screen opens at the state that claims nothing —
   * at the door, unread, unstopped — and the boot read or the stream's `done`
   * replaces it with what the server actually says.
   */
  const [gate, setGate] = useState<KnockGate>("at_door");
  const [assessed, setAssessed] = useState(false);
  const [guardClosed, setGuardClosed] = useState(false);
  /**
   * The composer starts empty — unless this knock began as words written
   * before there was an account, in which case it starts as those words. They
   * are put back in the sender's hands rather than posted for them, because
   * nobody pressed send on them here: a draft rescued across an identity
   * provider is a draft, and answering with it is the sender's act.
   *
   * `firstTurn` is the other case and is NOT this one — words whose Send was
   * pressed a moment ago, in the console, at this door. Those go up as a turn.
   */
  const [draft, setDraft] = useState(prefill);
  const [streaming, setStreaming] = useState<string | null>(null);
  const [considering, setConsidering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [booting, setBooting] = useState(opening === null);
  /**
   * The disclosure as the TRANSCRIPT READ found it, and nothing else.
   *
   * IT IS NOT SEEDED FROM THE PROP, and that is the bug a browser walk found:
   * `useState(disclosure)` captures whatever the prop was at MOUNT, and this
   * screen mounts the instant a knock id exists — which can be a render before
   * the start response it carries has been put in state. The line then never
   * appeared at all, because nothing afterwards could change a value that was
   * only ever read once. The prop wins where it has anything to say, this
   * fills in on a re-entry, and neither can shadow the other.
   */
  const [readLine, setReadLine] = useState("");
  const aiLine = disclosure || readLine;
  /** The transcript could not be read back: an error and a retry, never an
   * empty thread pretending nothing was said. */
  const [bootError, setBootError] = useState(false);
  const [bootAttempt, setBootAttempt] = useState(0);
  /** The budget ratio and whether the server wants the bar shown at all (R6). */
  const [budget, setBudget] = useState<{ ratio: number; show: boolean } | null>(null);
  /** The out-of-room moment, offered once per knock and dismissed locally. */
  const [outOfRoom, setOutOfRoom] = useState(false);
  /** Whether what is out of room is a PAUSED HEARING rather than a refused send. */
  const paused = useRef(false);
  /** The file going up right now, and the refusal if one came back. */
  const [pendingFile, setPendingFile] = useState<{ filename: string; size: number } | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  /** The itemised receipt, read back once the hearing is over (M11 fallback). */
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  /** The scored knock's standing (M12) — the terminal screen once it is known. */
  const [standing, setStanding] = useState<Standing | null>(null);
  /**
   * The conversation AFTER the door opened (owner, 2026-08-26). Its own draft
   * and its own busy flag, kept apart from the hearing's: a sender writing to a
   * person is not taking a turn, and sharing `draft` with the grilling composer
   * would carry half a sentence from one conversation into the other.
   */
  const [personDraft, setPersonDraft] = useState("");
  const [personBusy, setPersonBusy] = useState(false);
  const [personError, setPersonError] = useState<string | null>(null);

  /**
   * WHAT THE OUT-OF-ROOM ROW SAYS, READ FROM THE PLAN AND NOT FROM THE REFUSAL.
   *
   * The knock's turn stream refuses with a bare code — it carries no details at
   * all — so there is no 402 body here to read anything out of. The plan
   * payload carries the notice's line and the one control's label, both the
   * server's (wave 57); the priced offer behind the control is the plan
   * sheet's, and this screen never holds it.
   */
  const plan = useQuery(planQuery());

  const expired = useRef(onSessionExpired);
  expired.current = onSessionExpired;
  const gone = useRef(onGone);
  gone.current = onGone;
  const abort = useRef<AbortController | null>(null);
  /** The upload in flight, so dismissing the chip can stop it. */
  const uploading = useRef<AbortController | null>(null);
  /**
   * The moment has been offered. A ref rather than state because it is what
   * makes the row ONCE: room arriving puts `outOfRoom` back to false, and a
   * later `low` event must not read that as never having been asked.
   */
  const lowSeen = useRef(false);
  /**
   * The turn the server refused for want of room, waiting on room to arrive.
   *
   * A ref rather than state because nothing renders it — the words are already
   * on screen, in the composer they came out of — and because the thing that
   * reads it is `resetLanded`, run when room is added somewhere else entirely
   * (`lib/room.ts`). Cleared the moment it is spent, so a second, voluntary
   * upgrade later in the same hearing does not re-send a turn nobody asked to
   * take again.
   */
  const refused = useRef<string | { voiceId: string; words: string } | null>(null);

  // A tab that closes mid-answer must not leave a fetch talking to nothing.
  useEffect(() => {
    return () => {
      abort.current?.abort();
      uploading.current?.abort();
    };
  }, []);

  /** Whether this screen is still on the page, for the reads that outlive a render. */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * How many standings this screen has put up. A load that started before the
   * latest one landed reads a count that has moved, and drops what it fetched:
   * the stream's inline standing is newer than a read that set off before it.
   */
  const standingSeq = useRef(0);
  /** A standing load is out — and whether another was asked for meanwhile. */
  const loading = useRef(false);
  const loadAgain = useRef(false);

  const showStanding = useCallback((s: Standing) => {
    standingSeq.current += 1;
    setStanding(s);
    track({ name: "standing_shown", band: s.band });
  }, []);

  /**
   * THE ONE ROAD TO THE STANDING (M12), taken by every place that learns the
   * doorman has published: the boot read, the stream's `done` when it carried
   * no standing inline, the poll, the re-read after a refused turn, and the ask
   * again once room arrives. There used to be three inline copies of this and
   * two roads with none — the poll and the re-read set `assessed` and never
   * asked for the standing, so a read that arrived that way drew the fallback
   * card for good, which is what the owner found on staging (wave 75).
   *
   *   - the standing → the card;
   *   - `RESULT_HELD` → the out-of-room row (ruling R-0921-2 (6)): the read
   *     ran and is waiting on room, the same fact a paused hearing is about,
   *     with the same one control;
   *   - anything else → the receipt, under the fallback headline.
   *
   * ONE LOAD AT A TIME. A second ask while one is out does not start a second
   * fetch; it asks the one out to go round again when it lands, so the answer
   * on screen is never older than the last ask.
   */
  const loadStanding = useCallback(async (): Promise<void> => {
    if (loading.current) {
      loadAgain.current = true;
      return;
    }
    loading.current = true;
    try {
      do {
        loadAgain.current = false;
        const seq = standingSeq.current;
        const read = await fetchStanding(knockId);
        if (!mounted.current) return;
        // A newer standing landed while this one was out: it stands.
        if (seq !== standingSeq.current) continue;
        if (read.ok) showStanding(read.standing);
        else if (read.code === "RESULT_HELD") setOutOfRoom(true);
        else {
          const r = await fetchReceipt(knockId);
          if (mounted.current && r.ok) setReceipt(r.receipt);
        }
      } while (loadAgain.current);
    } finally {
      loading.current = false;
    }
  }, [knockId, showStanding]);

  /**
   * What the screen last knew of `assessed`, for the reads that run outside a
   * render — the poll and the re-read after a refused turn — so each can tell
   * "the doorman has just published" from "it published a while ago".
   */
  const assessedNow = useRef(assessed);
  assessedNow.current = assessed;

  /**
   * HOW MUCH OF THE SERVER'S TRANSCRIPT IS ALREADY ON SCREEN — the poll below
   * reads it, and it is a count rather than a flag because that is the only
   * thing that distinguishes "nothing new" from "one more line". Zero until a
   * read lands, which is right for a knock started in this tab: its lines came
   * from the stream, and the first poll that finds a longer server copy is
   * welcome to replace them with it.
   */
  const applied = useRef(0);
  /** The client-invented opening pair, if there is one — see the poll. */
  const prelude0 = useRef(prelude);
  prelude0.current = prelude;

  /**
   * Re-entry reads the conversation back. It writes nothing, so it is safe on
   * every entry — which is how a closed tab lands back in the same thread, with
   * its closing state if the grilling finished without it.
   */
  useEffect(() => {
    if (opening !== null) return;
    let live = true;

    void (async () => {
      const [knock, transcript] = await Promise.all([
        fetchKnock(knockId),
        fetchKnockMessages(knockId),
      ]);
      if (!live) return;

      if (!knock.ok) {
        if (knock.code === "UNAUTHENTICATED") {
          expired.current();
          return;
        }
        if (knock.code === "KNOCK_NOT_FOUND") {
          gone.current();
          return;
        }
        setBooting(false);
        setError(RETRY_COPY);
        return;
      }

      setGate(knock.knock.gate);
      setAssessed(knock.knock.assessed);
      setGuardClosed(knock.knock.guard_closed);
      setUsed(knock.knock.turns_used);
      setOf(knock.knock.max_turns);
      if (!transcript.ok) {
        // The knock exists but its conversation could not be read: rendering
        // an empty thread would claim nothing was said. Say the read failed.
        setBooting(false);
        setBootError(true);
        return;
      }
      if (transcript.disclosure) setReadLine(transcript.disclosure);
      // The transcript is words; the attachments are files; the work is what
      // the doorman did. Each carries the moment it happened, and a re-entry
      // draws them in that order — which is where the live thread drew them
      // (wave 82). A voice note sent AS a turn is drawn on its line (wave 76).
      applied.current = transcript.messages.length;
      setLines(transcriptLines(transcript.messages, knock.knock.attachments, knock.knock.work));
      // A read published while the tab was closed is a standing to fetch (M12).
      if (knock.knock.assessed) await loadStanding();
      setBooting(false);
    })();

    return () => {
      live = false;
    };
  }, [knockId, opening, bootAttempt, loadStanding]);

  /**
   * THE SENDER'S SIDE IS LIVE TOO (`docs/conversation-model.md` §3, §6.1).
   *
   * Nothing pushes to this screen. Until this clock existed it read the knock
   * once, on mount, and after a turn that failed — so a door opened while the
   * sender was looking at it stayed shut on their screen, and the reply that
   * opened it sat on the server until they reloaded the page. The owner watched
   * exactly that on staging.
   *
   * TWO READS. The knock is asked every tick and is cheap: it carries the gate,
   * which is the fact the composer hangs off and the only way this screen
   * learns it was let in. The TRANSCRIPT is asked too, at every gate but
   * `blocked` (ruling R-0925-2 (3)): it used to be asked only at `let_in`, on
   * the reasoning that at the door the doorman's lines arrive over the stream —
   * and a question the owner had Nob relay does not, so on staging it sat on
   * the server while the knocker looked at their open page. The poll never
   * fights the stream: it stands down while a turn is streaming (below).
   *
   * It stands down while a turn is streaming, while a write-back is in flight
   * and while the screen is still booting: all three own `lines` at that
   * moment, and a poll landing on top of one of them would take a bubble back
   * off the screen. `blocked` stops it for good — there is nothing coming.
   */
  /**
   * WHAT THIS PANE LEARNS IS NEWS TO MORE THAN THIS PANE — AND IT RIDES ON THE
   * FACTS THEMSELVES (gauntlet walk 2026-09-13, F-14; contract F-58; a U-5 and
   * b unnamed 3, wave 17b).
   *
   * The composer hangs off `gate`: at `let_in` the doorman's lane closes and
   * the write-back lane to the person opens. The sidebar row, the thread
   * header's chip and the desk's Stage hang off the THREADS list, which is a
   * cached read nothing here pushes into. This invalidation used to live inside
   * the poll's tick, which meant it fired only when the poll was the thing that
   * learned the news — so a screen that learned it from its own boot read, or
   * from a refused turn (`turnFailureLine` → null → re-read), swapped the
   * composer to "Message Sam" and left the desk one column over saying "You are
   * still talking to their doorman" until the tab was reloaded.
   *
   * IT WATCHED ONLY THE GATE, and the gate is not the only thing the list draws
   * from. `inFlight` — the fact behind "Hearing in progress" on the chip and in
   * the Stage row — is `!assessed && gate === at_door && !guard_closed`. So
   * when the doorman PUBLISHED, which moves `assessed` and not the gate, the
   * standing card landed in this pane and the header above it and the desk
   * beside it went on saying the hearing was still running. A reload settled
   * both, which is the signature of a list nobody told.
   *
   * So all three facts are watched, in one place. Every road that moves any of
   * them — the poll, the boot, the stream's `done`, the re-read after a refused
   * turn — already writes them into this state, and the list moves with them
   * exactly once per change.
   */
  const invalidated = useRef({ gate, assessed, guardClosed });
  useEffect(() => {
    const was = invalidated.current;
    if (was.gate === gate && was.assessed === assessed && was.guardClosed === guardClosed) return;
    invalidated.current = { gate, assessed, guardClosed };
    void queryClient.invalidateQueries({ queryKey: qk.threads() });
  }, [gate, assessed, guardClosed, queryClient]);

  const listening = !booting && !busy && !personBusy && gate !== "blocked";
  useEffect(() => {
    if (!listening) return;
    let live = true;
    const tick = async () => {
      const knock = await fetchKnock(knockId);
      if (!live || !knock.ok) return;
      setGate(knock.knock.gate);
      // The doorman published while nothing here was streaming — the clock's
      // read (§5.3), say. The standing is fetched, not left to the fallback.
      if (knock.knock.assessed && !assessedNow.current) void loadStanding();
      setAssessed(knock.knock.assessed);
      setGuardClosed(knock.knock.guard_closed);
      // R-0925-2 (3): the doorman speaks unasked at the door too — a question
      // the owner had it relay, or the owner's own close at the read — so the
      // transcript is re-read at every gate but `blocked`, not only `let_in`.
      if (knock.knock.gate === "blocked") return;
      const transcript = await fetchKnockMessages(knockId);
      // Nothing new is the common answer, and it must leave the screen alone:
      // replacing the lines with an identical copy would drop the sender's own
      // optimistic bubble for a frame every four seconds.
      if (!live || !transcript.ok || transcript.messages.length <= applied.current) return;
      applied.current = transcript.messages.length;
      setLines([
        ...(prelude0.current ?? []),
        ...transcriptLines(transcript.messages, knock.knock.attachments, knock.knock.work),
      ]);
      // …and the list beside this pane moves with it: the knock's stamp moved
      // on the server when the line landed, and the row is a cached read.
      void queryClient.invalidateQueries({ queryKey: qk.threads() });
    };
    const timer = setInterval(() => void tick(), LIVE_THREAD_REFRESH_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [listening, knockId, queryClient, loadStanding]);

  /**
   * The one streaming consumer. `mine` is the sender's optimistic line, which is
   * also what comes back out of the transcript if the turn never lands.
   *
   * `voiceId` makes the turn a VOICE NOTE (wave 76): the server reads the
   * note's transcript as the words. A voice turn that does not land leaves its
   * line where it is — the note IS in the hearing, uploaded, and the doorman
   * will be told of it — and puts nothing in the composer: nobody typed it.
   */
  const drive = useCallback(
    (message: string, mine: ThreadLine | null, voiceId: string | null = null) => {
      setError(null);
      setBusy(true);
      setConsidering(true);
      setStreaming(null);

      const controller = new AbortController();
      abort.current = controller;

      void (async () => {
        let said = "";
        let turn = used + 1;
        let settled = false;
        /** The standing carried inline as a read is published (M12), if one was. */
        let published: Standing | null = null;

        /**
         * Whatever the doorman has finished saying joins the transcript —
         * TRIMMED, as the server stores it (wave 82). The stream carries the
         * whitespace the model wrote before its trailer, and a bubble that
         * kept it drew an empty band under its words ("No." three lines tall)
         * that a reload, reading the stored line, did not.
         */
        const commit = () => {
          const spoken = said.trim();
          said = "";
          setStreaming(null);
          if (!spoken) return;
          setLines((prev) => [...prev, { role: "doorman", content: spoken }]);
        };

        /** The turn did not land: the words go back where they were typed. */
        const stumble = (line: string | null) => {
          settled = true;
          if (mine && voiceId === null) {
            setLines((prev) => prev.filter((l) => l !== mine));
            setDraft((current) => (current.trim() ? current : message));
          }
          setStreaming(null);
          setConsidering(false);
          setInFlight(null);
          setBusy(false);
          setError(line);
        };

        for await (const event of streamTurn(knockId, message, controller.signal, voiceId)) {
          switch (event.type) {
            case "state":
              setConsidering(event.status === "listening");
              if (event.turn > 0) {
                turn = event.turn;
                setInFlight(event.turn);
              }
              if (event.of > 0) setOf(event.of);
              break;

            case "token":
              said += event.delta;
              setConsidering(false);
              setStreaming(said);
              break;

            /**
             * **THE HEARING IS PAUSED** (ruling R-0921-2 (5)). Not a refusal:
             * the knock is open, what the sender wrote is on the server, and
             * nothing was spent. So the composer is left alone — no stumble, no
             * line put back in the box — and the card that carries the three
             * roads is drawn over the thread. The retry is the one
             * `resetLanded` already performs, and it sends an EMPTY message,
             * because the turn being resumed is the one already on disk.
             */
            case "paused":
              paused.current = true;
              setOutOfRoom(true);
              setConsidering(false);
              break;

            /** Room arrived and the hearing picked up where it stopped. */
            case "resumed":
              paused.current = false;
              setOutOfRoom(false);
              break;

            case "retract":
              // R-0921-1: the provider dropped the stream a few words in and
              // the server is asking again. What is on screen was never a
              // reply, so it goes — and the room is thinking again, which is
              // what it was doing before the first token arrived. Nothing is
              // said about it: a sender who blinked saw nothing happen.
              said = "";
              setStreaming(null);
              setConsidering(true);
              break;

            case "notice":
              // A notice interrupts: whatever was being said is finished first,
              // so the room's line never lands mid-sentence.
              commit();
              setLines((prev) => [...prev, { role: "notice", content: event.content }]);
              break;

            case "close":
              // R-0925-2 (2): the owner's own line for this kind of knocker,
              // sent at the read. The reply is finished first, and the close
              // is the doorman's next bubble — as a reload reads it back.
              commit();
              setLines((prev) => [...prev, { role: "doorman", content: event.content }]);
              break;

            case "work_started": {
              // A piece of work opens its own line IN THE THREAD, under the
              // doorman line that asked for it (wave 82). Whatever it had said
              // is finished first, so its words and what it says once the work
              // is back are two bubbles with the work between them — exactly
              // as the stored transcript reads them back on a reload. Work,
              // never money (FR-3.5).
              commit();
              setConsidering(false);
              const started: WorkItemState = {
                id: event.work_item_id,
                kind: event.kind,
                label: event.label,
                units: event.units,
                progress: null,
                done: false,
                units_executed: 0,
                outcome: {},
              };
              setLines((prev) => [
                ...prev.filter((l) => l.work?.id !== event.work_item_id),
                { role: "notice", content: "", work: started },
              ]);
              break;
            }

            case "work_progress":
              // The base label stays; the count is derived from the fraction.
              setLines((prev) =>
                prev.map((l) =>
                  l.work?.id === event.work_item_id
                    ? { ...l, work: { ...l.work, progress: event.progress } }
                    : l,
                ),
              );
              break;

            case "work_completed":
              setLines((prev) =>
                prev.map((l) =>
                  l.work?.id === event.work_item_id
                    ? {
                        ...l,
                        work: {
                          ...l.work,
                          done: true,
                          label: event.label,
                          units_executed: event.units_executed,
                          outcome: event.outcome,
                        },
                      }
                    : l,
                ),
              );
              break;

            case "budget":
              setBudget({ ratio: event.consumed_ratio, show: event.show_bar });
              // The moment, offered once. The doorman's own notice line arrives
              // separately; this is the card of three roads under it, and a
              // knock that has already had it does not get it again.
              if (event.low && !lowSeen.current) {
                lowSeen.current = true;
                setOutOfRoom(true);
              }
              break;

            case "standing":
              // The read, carried inline before `done` (M12). Held until the
              // server confirms it was published so it lands with its card —
              // and it arrives on EVERY published assessment (§5.2), so a turn
              // that moved the doorman's mind simply replaces the last one.
              published = event.standing;
              break;

            case "done":
              settled = true;
              commit();
              setConsidering(false);
              setInFlight(null);
              setBusy(false);
              setError(null);
              // NOT A STATUS, AND THE GATE IS NOT TOUCHED. The stream reports
              // what the doorman did; where this person stands is the
              // recipient's to move and nothing here may move it (§3).
              setAssessed(event.assessed);
              setGuardClosed(event.guard_closed);
              setUsed(turn);
              track({ name: "turn_completed", turn });
              // The stream is not a query — but the turn it just settled drew
              // on this period and changed this knock, and both are read as
              // cached facts elsewhere (the header's ring, a re-entry).
              void queryClient.invalidateQueries({ queryKey: qk.plan() });
              void queryClient.invalidateQueries({ queryKey: qk.knock(knockId) });
              // A read was published. Prefer the standing carried inline; the
              // stream carries none when the result is held (ruling R-0921-2
              // (6)), and then the loader asks and is told so.
              if (event.assessed) {
                if (published) showStanding(published);
                else void loadStanding();
              }
              break;

            case "error": {
              if (event.code === "UNAUTHENTICATED") {
                settled = true;
                expired.current();
                return;
              }
              if (event.code === "KNOCK_NOT_FOUND") {
                settled = true;
                gone.current();
                return;
              }
              /**
               * THE PLAN COULD NOT PAY FOR THIS TURN (§4.4, F-10's chain).
               *
               * Every turn is metered as it lands, and this is the server
               * refusing one for want of room in the period. It is not a
               * stumble and must never be dressed as one — a sender told to
               * "say that again" retypes into the same refusal for ever, which
               * is exactly how this was found.
               *
               * So: no line at all, the out-of-room row in the thread, the words
               * back in the composer they were typed in, and the turn goes up BY
               * ITSELF the moment there is room again. That is the knock-start
               * 402 → paywall → checkout → automatic retry chain, one screen
               * further in; the sender pressed send once and should not have to
               * press it again to be heard.
               *
               * `ALLOWANCE_PAUSED` is the same wall met from the other side: the
               * hearing was already paused — a tab that came back to it, say —
               * and a new line was sent into it (wave 57's browser walk found
               * it answered with the stumble). The words go back in the box as
               * above, and what room resumes is the paused turn, with nothing.
               */
              if (event.code === "PLAN_EXHAUSTED" || event.code === "ALLOWANCE_PAUSED") {
                stumble(null);
                if (event.code === "ALLOWANCE_PAUSED") paused.current = true;
                else refused.current = voiceId ? { voiceId, words: message } : message;
                setOutOfRoom(true);
                // The row says the notice's own line, and the cached read may
                // predate the wall this turn just met.
                void queryClient.invalidateQueries({ queryKey: qk.plan() });
                return;
              }
              const line = turnFailureLine(event.code);
              stumble(line);
              // The doorman stopped listening under us — the door opened, the
              // guard fired, or this sender was blocked. Only the server knows
              // which, so it is asked rather than assumed.
              if (line === null) {
                const knock = await fetchKnock(knockId);
                if (knock.ok) {
                  setGate(knock.knock.gate);
                  if (knock.knock.assessed && !assessedNow.current) void loadStanding();
                  setAssessed(knock.knock.assessed);
                  setGuardClosed(knock.knock.guard_closed);
                  setUsed(knock.knock.turns_used);
                  setOf(knock.knock.max_turns);
                }
              }
              return;
            }
          }
        }

        // The stream stopped without saying so: no turn was taken, and no turn
        // taken is a turn worth trying again.
        if (!settled && !controller.signal.aborted) stumble(RETRY_COPY);
      })();
    },
    [knockId, used, queryClient, showStanding, loadStanding],
  );

  /**
   * WHETHER THE SENDER MAY STILL SPEAK TO THE DOORMAN (§6.1), and the read is
   * not on the list: publishing one changes nothing about the composer. Two
   * things stop a turn — being blocked, and the guard having stopped the
   * doorman listening — and a third, `let_in`, is not a refusal but a handover:
   * the write-back lane below is the road there.
   */
  const canSpeak = (gate === "at_door" || gate === "declined") && !guardClosed;

  /**
   * `text` is what the composer composed — the draft, plus the quotation it is
   * answering if there is one (§ `lib/quote.ts`). It is handed in rather than
   * read off `draft` because the quote is assembled at the moment of sending
   * and would otherwise be a render behind.
   */
  const send = useCallback(
    (text: string) => {
      const message = text.trim();
      if (!message || busy || !canSpeak) return;

      // The sender's line goes up the moment they send it, and it is this same
      // object that comes back out of the transcript if the turn never lands.
      const mine: ThreadLine = { role: "sender", content: message };
      setLines((prev) => [...prev, mine]);
      setDraft("");
      drive(message, mine);
    },
    [busy, canSpeak, drive],
  );

  /**
   * THE FIRST TURN SENDS ITSELF (owner item 7, 2026-08-27) — see `firstTurn`.
   *
   * ONCE, and by a ref rather than by a dependency list, because `send` is
   * rebuilt on every turn taken: a guard that lived in the deps would re-fire
   * this the moment the first answer landed and post the same words again.
   *
   * A conversation that cannot take a turn hands the words BACK instead of
   * swallowing them — the same fallback a stumbled turn takes. It should not
   * happen (a knock is at the door the instant it is started), which is exactly
   * why it must not lose anything when it does.
   */
  const autoSent = useRef(false);
  useEffect(() => {
    if (autoSent.current || booting) return;
    const words = firstTurn.trim();
    if (!words) return;
    autoSent.current = true;
    if (canSpeak) send(words);
    else setDraft((current) => (current.trim() ? current : words));
  }, [booting, canSpeak, firstTurn, send]);

  /**
   * The two moving parts of the refused turn's retry, held as refs so the
   * checkout's callback reads them as they are when it fires rather than as
   * they were when the card was opened. `draft` is preferred over the refused
   * message because the composer kept the words and they are the sender's to
   * edit while the modal is up: sending the original over an edit would be the
   * product overruling somebody who had just changed their mind.
   */
  const sendNow = useRef(send);
  sendNow.current = send;
  /** The stream, as it is when an upload lands — see `putUp`. */
  const driveNow = useRef(drive);
  driveNow.current = drive;
  const draftNow = useRef(draft);
  draftNow.current = draft;

  /**
   * A document, on its way up the moment it is chosen (§6.3). The sender keeps
   * typing while it goes; the chip is the only thing that knows about it.
   *
   * Both limits are checked here first — 25 MB and the six extensions — so the
   * two most common refusals are answered without the file ever leaving the
   * browser. Everything else is the server's to refuse, one sentence per code.
   */
  /**
   * The upload itself, shared by the paperclip and by the mic.
   *
   * `asTurn` is the mic's (wave 76): a voice note is a MESSAGE, so once it has
   * landed with a transcript it goes up as the sender's turn, drawn as one
   * bubble — the player, the words under it. A note the transcriber could not
   * hear sends nothing: it stays in the thread as the file it is, and Nob says
   * so once, in its own voice, and asks for it typed.
   */
  const putUp = useCallback(
    (file: File, durationMs: number | null, asTurn = false) => {
      setAttachError(null);
      setPendingFile({ filename: file.name, size: file.size });

      const controller = new AbortController();
      uploading.current = controller;

      void (async () => {
        const r = await uploadAttachment({ knockId }, file, controller.signal, durationMs);
        // The sender changed their mind while it was going up: whatever the
        // server said about a file nobody wants is not news.
        if (controller.signal.aborted) return;
        uploading.current = null;
        setPendingFile(null);

        if (!r.ok) {
          if (r.code === "UNAUTHENTICATED") {
            expired.current();
            return;
          }
          if (r.code === "KNOCK_NOT_FOUND") {
            gone.current();
            return;
          }
          setAttachError(attachmentFailureLine(r.code));
          return;
        }
        const note = r.attachment;
        if (asTurn && note.transcript !== null) {
          const mine: ThreadLine = { role: "sender", content: note.transcript, attachment: note };
          setLines((prev) => [...prev, mine]);
          driveNow.current(note.transcript, mine, note.attachment_id);
          return;
        }
        // Into the thread, where it was said: a document is a sender's line,
        // and it stays at this moment of the conversation as later lines land.
        setLines((prev) => [...prev, { role: "sender", content: "", attachment: note }]);
        if (asTurn) setError(VOICE_UNHEARD_COPY);
      })();
    },
    [knockId],
  );

  const attach = useCallback(
    (file: File) => {
      if (pendingFile) {
        setAttachError(ONE_AT_A_TIME);
        return;
      }
      const refused = localRefusal(file);
      if (refused) {
        setAttachError(attachmentFailureLine(refused));
        return;
      }
      putUp(file, null);
    },
    [pendingFile, putUp],
  );

  /**
   * A recording, up the same road as a document (owner decision 2026-08-22)
   * — and then sent as the sender's turn, because a voice note is a message
   * (wave 76; `putUp`'s `asTurn`).
   *
   * No `localRefusal`: that function checks a file's extension against the
   * PICKER's list, and a voice note is not something the sender picked — the
   * recorder named it, from the type it actually wrote. The server's allowlist
   * is still the one that decides, exactly as it does for a PDF.
   */
  const voiceNote = useCallback(
    (clip: { blob: Blob; filename: string; durationMs: number }) => {
      if (pendingFile) {
        setAttachError(ONE_AT_A_TIME);
        return;
      }
      putUp(new File([clip.blob], clip.filename, { type: clip.blob.type }), clip.durationMs, true);
    },
    [pendingFile, putUp],
  );

  /** The ✕ on the chip: stop the upload, and forget it happened. */
  const dropAttachment = useCallback(() => {
    uploading.current?.abort();
    uploading.current = null;
    setPendingFile(null);
  }, []);

  /**
   * WHETHER THE DOOR OPENED — THE GATE, AND NOTHING THAT LOOKS LIKE IT (§6.1).
   *
   * `let_in` is the recipient's own decision, it is on the sender's own view,
   * and it is the ONLY thing the server will honour: `getWriteBackTarget` asks
   * the gate and refuses everything else with a 404.
   *
   * A `recipient` LINE USED TO COUNT HERE AND IT MUST NOT. The reasoning was
   * that the person behind the door had visibly spoken, so the composer should
   * not lag the conversation — but a reply can reach this transcript while the
   * gate is still at the door (that is exactly what the half-opened knock of
   * 2026-09-09 looked like), and the screen then offered a lane the server
   * refused. A composer that cannot post is worse than a composer that arrives
   * one poll late.
   *
   * The mutual notice stays, and it is a different animal: the room only ever
   * writes it having put BOTH gates to `let_in` in one operation (§9), so it is
   * a statement about the gate rather than a guess from a message.
   */
  const letThrough =
    gate === "let_in" ||
    lines.some((line) => line.role === "notice" && line.content === MUTUAL_KNOCK_NOTICE);

  const sendToPerson = useCallback(() => {
    const message = personDraft.trim();
    if (!message || personBusy) return;
    setPersonBusy(true);
    setPersonError(null);
    setPersonDraft("");
    // Optimistic, and it is the sender's own words: if the post fails they are
    // handed back to the field rather than left on screen as a message that
    // never went.
    const mine: ThreadLine = { role: "sender", content: message };
    setLines((prev) => [...prev, mine]);

    void (async () => {
      const r = await sendKnockMessage(knockId, message);
      setPersonBusy(false);
      if (r.ok) return;
      setLines((prev) => prev.filter((line) => line !== mine));
      setPersonDraft((current) => (current.trim() ? current : message));
      if (r.code === "UNAUTHENTICATED") {
        expired.current();
        return;
      }
      // The server's own word for it, in the sender's terms: a 404 here is a
      // door that is not open, and only a 5xx or a dead line is "say it again".
      setPersonError(r.code === "KNOCK_NOT_FOUND" ? PERSON_SEND_CLOSED : PERSON_SEND_FAILED);
    })();
  }, [knockId, personBusy, personDraft]);

  /**
   * THERE IS NO "CONTINUE" AND NOTHING FOR IT TO OPEN (§10). It used to start a
   * second knock at this door and hand the words up as its first turn; there is
   * one knock per sender and door now, it never closes, and the ordinary
   * composer above IS the way on — a turn after the read is metered as it lands
   * (§4.4) and the doorman answers in the same conversation.
   */

  /**
   * ROOM ARRIVED, AND THE HEARING CARRIES ON BY ITSELF (F-10).
   *
   * The purchase itself belongs to the plan sheet — "Get more usage" opens it —
   * so all that is left here is what the hearing owes the person afterwards. It used to be a whole checkout: a pack, an auto-reload rider, a
   * Stripe modal over a live stream, and the knock target stored in case the
   * browser left the origin. Ruling R-0916-1 deleted every part of that. Since
   * wave 57 it runs when room is added anywhere (`lib/room.ts`) — a plan
   * bought, a plan moved up — while this hearing is out of room. (A paid reset
   * was a third way until ruling R-0923-3 (1) deleted it.)
   *
   * Never a "try again" control: the sheet was opened BY the refusal, so the
   * sender is standing in front of the thing they were waiting for, and asking
   * them to press send a second time is the product forgetting what it was
   * doing.
   */
  const resetLanded = useCallback(() => {
    // The share of the period moved, and the ring in the header reads it.
    void queryClient.invalidateQueries({ queryKey: qk.plan() });
    setOutOfRoom(false);
    /**
     * A PAUSED HEARING RESUMES WITH NOTHING (ruling R-0921-2 (5)). The turn it
     * is picking up is the one the server already has: sending the words again
     * would ask for a second turn and pay for two. A refused SEND is the other
     * case and is unchanged — nothing reached the server, so the words go.
     *
     * Straight to the stream, not through `send`: `send` refuses an empty
     * message by design (a composer never sends one), so the resume it was
     * handed here never went up at all — found by wave 57, once room could be
     * added somewhere other than a card inside this thread.
     */
    if (paused.current) {
      paused.current = false;
      refused.current = null;
      drive("", null);
      return;
    }
    const words = refused.current;
    refused.current = null;
    // A refused VOICE turn goes up as the note again (wave 76): its line is
    // still on screen and the composer never held its words.
    if (words && typeof words !== "string") {
      drive(words.words, null, words.voiceId);
      return;
    }
    if (words) {
      sendNow.current(draftNow.current.trim() || words);
      return;
    }
    /**
     * A HELD RESULT NEEDS NO TURN, ONLY THE ASK AGAIN (ruling R-0921-2 (6)).
     * Nothing was refused and nothing is paused: the hearing is decided and the
     * standing was being withheld. The room is there now, so the read is
     * fetched — and the route lifts the hold as it serves it.
     */
    void loadStanding();
  }, [queryClient, drive, loadStanding]);
  useRoomAdded(() => {
    if (outOfRoom) resetLanded();
  });

  if (booting || bootError) {
    return (
      <ChatShell
        fill={embedded}
        header={
          embedded ? null : (
            <DoorHeader
              name={doorName}
              handle={handle}
              onBack={onLeave}
              backLabel={BACK_TO_DOOR_COPY}
            />
          )
        }
      >
        {bootError ? (
          <>
            <SystemRow>Nob is not answering. Try that again.</SystemRow>
            <p className="mt-2 text-center">
              <QuietButton
                type="button"
                onClick={() => {
                  setBootError(false);
                  setBooting(true);
                  setBootAttempt((n) => n + 1);
                }}
              >
                Try again
              </QuietButton>
            </p>
          </>
        ) : (
          <SystemRow breathe>{BOOTING_COPY}</SystemRow>
        )}
      </ChatShell>
    );
  }

  return (
    <KnockThread
      handle={handle}
      doorName={doorName}
      doorAvatar={doorAvatar}
      // The doorman's read (M12) is a card in the thread and not a screen that
      // replaces it. A guard-closed knock has none, and neither does a read
      // whose standing could not be fetched, which keeps the bare fallback.
      standing={standing}
      // Until the read is published the number shown is the turn being taken;
      // after it the meter stops (`turnMeta`), so this is the count that was.
      turn={inFlight ?? (assessed ? used : Math.min(used + 1, of || used + 1))}
      of={of}
      lines={lines}
      streaming={streaming}
      considering={considering}
      gate={gate}
      assessed={assessed}
      guardClosed={guardClosed}
      error={error}
      draft={draft}
      onDraft={setDraft}
      onSend={send}
      busy={busy}
      onLeave={onLeave}
      budget={budget}
      usagePlan={plan.data?.plan ?? null}
      outOfRoom={outOfRoom}
      {...(onOpenPlans ? { onSeePlans: onOpenPlans } : {})}
      trialOffer={plan.data?.trial ?? null}
      {...(onStartTrial ? { onStartTrial } : {})}
      pendingAttachment={pendingFile}
      attachmentError={attachError}
      onAttach={attach}
      onVoiceNote={voiceNote}
      onDismissAttachment={dropAttachment}
      receipt={receipt}
      knockId={knockId}
      embedded={embedded}
      disclosure={aiLine}
      above={above}
      personTalk={
        letThrough
          ? {
              // The door's name is who is on the other end. It falls back to the
              // handle rather than to nothing: a send button must name somebody.
              name: doorName?.trim() || `@${handle}`,
              draft: personDraft,
              onDraft: setPersonDraft,
              onSend: sendToPerson,
              busy: personBusy,
              error: personError,
            }
          : null
      }
    />
  );
}
